'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { childAvailabilityQuery, getChild, getChildAvailabilityById } from '@sola/proxy-sdk/children.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import { MOCK_SUPPORTER } from '@sola/supporter-sdk/constants.ts';
import { MY_QUERY_KEYS } from '../constants.ts';
import { getMockChildDetails } from './mocks.ts';
type ChildDetailsData = NonNullable<Awaited<ReturnType<typeof getChild>>>;

/**
 * Hook to fetch and cache child details by child ID.
 */
export const useChildDetails = childId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== childId) {
    t0 = [MY_QUERY_KEYS.childDetails, childId];
    t1 = async () => {
      if (!childId) {
        throw new Error("Missing child id.");
      }
      if (MOCK_SUPPORTER) {
        const childDetails = getMockChildDetails({
          childId,
          supporter: MOCK_SUPPORTER
        });
        if (!childDetails) {
          throw new Error(`Missing mock child details for child id "${childId}".`);
        }
        return childDetails;
      }
      const childDetails_0 = await getChild(childId);
      if (!childDetails_0) {
        throw new Error("Empty child details response.");
      }
      return childDetails_0;
    };
    $[0] = childId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!childId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      refetchOnWindowFocus: false,
      staleTime: Infinity
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};

/**
 * Hook to fetch and cache child availability item by child ID.
 */
export const useChildAvailability = childId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== childId) {
    t0 = [MY_QUERY_KEYS.childAvailability, childId];
    t1 = async () => {
      if (!childId) {
        throw new Error("Missing child id.");
      }
      const availability = await getChildAvailabilityById({
        globalId: childId
      });
      if (!availability) {
        throw new Error("Empty child availability response.");
      }
      return availability;
    };
    $[0] = childId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!childId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      refetchOnWindowFocus: false,
      staleTime: Infinity
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export interface UseSameCenterChildAvailabilityProps {
  centerId?: string | null;
  excludeChildId?: string | null;
  limit?: number;
}
export const DefaultSameCenterLimit = 2;
interface NormalizedSameCenterChildAvailabilityProps {
  centerId?: string;
  excludeChildId?: string;
  limit: number;
}
export const normalizeSameCenterChildAvailabilityProps = ({
  centerId,
  excludeChildId,
  limit = DefaultSameCenterLimit
}: UseSameCenterChildAvailabilityProps): NormalizedSameCenterChildAvailabilityProps => ({
  centerId: centerId ?? undefined,
  excludeChildId: excludeChildId ?? undefined,
  limit
});
const buildSameCenterChildAvailabilityQueryKey = (normalizedProps: NormalizedSameCenterChildAvailabilityProps) => {
  return [MY_QUERY_KEYS.sameCenterChildAvailability, normalizedProps.centerId, normalizedProps.excludeChildId, normalizedProps.limit] as const;
};
export const getSameCenterChildAvailabilityQueryKey = (props: UseSameCenterChildAvailabilityProps) => buildSameCenterChildAvailabilityQueryKey(normalizeSameCenterChildAvailabilityProps(props));

/**
 * Hook to fetch and cache available children at the same center.
 */
export const useSameCenterChildAvailability = t0 => {
  const $ = _c(14);
  const {
    centerId,
    excludeChildId,
    limit
  } = t0;
  let normalizedProps;
  let t1;
  let t2;
  if ($[0] !== centerId || $[1] !== excludeChildId || $[2] !== limit) {
    normalizedProps = normalizeSameCenterChildAvailabilityProps({
      centerId,
      excludeChildId,
      limit
    });
    t2 = useQuery;
    t1 = buildSameCenterChildAvailabilityQueryKey(normalizedProps);
    $[0] = centerId;
    $[1] = excludeChildId;
    $[2] = limit;
    $[3] = normalizedProps;
    $[4] = t1;
    $[5] = t2;
  } else {
    normalizedProps = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== normalizedProps.centerId || $[7] !== normalizedProps.excludeChildId || $[8] !== normalizedProps.limit) {
    t3 = async () => {
      if (!normalizedProps.centerId) {
        throw new Error("Missing center id.");
      }
      const results = await childAvailabilityQuery({
        numberOfChildren: normalizedProps.limit + (normalizedProps.excludeChildId ? 1 : 0),
        filter: [{
          field: "ICPId",
          operator: "equalto",
          value: [normalizedProps.centerId]
        }]
      });
      return (results?.children ?? []).filter(child => child.globalId !== normalizedProps.excludeChildId).slice(0, normalizedProps.limit);
    };
    $[6] = normalizedProps.centerId;
    $[7] = normalizedProps.excludeChildId;
    $[8] = normalizedProps.limit;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = !!normalizedProps.centerId;
  let t5;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      queryKey: t1,
      queryFn: t3,
      enabled: t4,
      refetchOnWindowFocus: false,
      staleTime: Infinity
    };
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t2(t5);
};